import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './errors';

/**
 * Creates the shared query client.
 *
 * A factory rather than a module-level singleton so every test gets a clean
 * cache and cannot leak state into the next one.
 */
export function createQueryClient(): QueryClient {
    return new QueryClient({
        defaultOptions: {
            queries: {
                // Coaching data changes on human timescales, so a short window
                // of staleness avoids refetching the dashboard on every focus
                // change without ever showing genuinely old numbers.
                staleTime: 30_000,
                gcTime: 5 * 60_000,

                retry: (failureCount, error) => {
                    // Retrying a 401/403/404/422 just repeats the same answer
                    // more slowly. Only transport and server faults are worth
                    // a second attempt.
                    if (error instanceof ApiError && !error.isRetryable) {
                        return false;
                    }

                    return failureCount < 2;
                },
                retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),

                refetchOnWindowFocus: true,
                // A phone that reconnects mid-workout should pick the data back
                // up without the user doing anything.
                refetchOnReconnect: true,
            },

            mutations: {
                // A write is never retried automatically: without idempotency
                // keys a retry could log the same workout twice.
                retry: false,
            },
        },
    });
}
